import { api } from 'core/axios/axios';
import type { Design } from './types/editor.types';
import { exportHtml, exportJson } from './services/export';

export interface ITemplateUploadUrls {
  jsonUrl: string;
  jsonFields: Record<string, string>;
  htmlUrl: string;
  htmlFields: Record<string, string>;
  template_key: string;
  file_secret: string;
}

export interface ITemplateUpdateUrls {
  jsonUrl: string;
  jsonFields: Record<string, string>;
  htmlUrl: string;
  htmlFields: Record<string, string>;
  file_secret: string;
}

export interface ITemplateDetail {
  data: string;
  /** Presigned URL to fetch the rendered HTML (no auth) */
  html: string;
  name: string;
  tag: string;
}

export interface ITemplateMeta {
  id: number;
  name: string;
  tag?: string;
  tag_name?: string;
  description?: string;
  default?: boolean;
  /** Spec field names from List Templates response */
  createdDate?: string;
  modifiedDate?: string;
  /** Fallback aliases — accept either naming convention */
  created_at?: string;
  updated_at?: string;
  [key: string]: unknown;
}

export interface IListTemplatesResponse {
  All_templates: Record<string, ITemplateMeta>;
  Total_templates: number;
}

function unwrap<T extends object>(raw: unknown, key: keyof T): T {
  if (raw && typeof raw === 'object' && !(key in (raw as object))) {
    const nested = (raw as Record<string, unknown>)['data'];
    if (nested && typeof nested === 'object' && key in (nested as object)) {
      return nested as T;
    }
  }
  return raw as T;
}

export async function uploadToS3(
  url: string,
  fields: Record<string, string>,
  blob: Blob,
  filename: string,
): Promise<void> {
  const form = new FormData();

  for (const [key, value] of Object.entries(fields || {})) {
    form.append(key, value);
  }
  // File MUST be the LAST field
  form.append('file', blob, filename);

  const res = await fetch(url, {
    method: 'POST',
    body: form,
  });

  if (res.status !== 204) {
    const text = await res.text().catch(() => 'No response body');
    console.error(`S3 upload failed — expected 204, got ${res.status}:`, text);
    throw new Error(`S3 upload failed with status ${res.status}`);
  }
}

export async function getUploadUrls(template_name: string, tag?: string): Promise<ITemplateUploadUrls> {
  const params: Record<string, string> = { template_name };
  if (tag) params.tag = tag;
  const { data } = await api.get('template-editor-url', { params });
  return unwrap<ITemplateUploadUrls>(data, 'jsonUrl');
}

export async function confirmSave(payload: {
  name: string;
  tag_name: string;
  description: string;
  template_key: string;
  post_status: string;
  file_secret: string;
  default: boolean;
  createdUser?: string;
}): Promise<ITemplateMeta> {
  const { data } = await api.post('template-editor-storage', payload);
  return data;
}

export async function getTemplateDetail(template_id: number | string): Promise<ITemplateDetail> {
  const { data } = await api.get('template-editor-storage', { params: { template_id } });
  return unwrap<ITemplateDetail>(data, 'data');
}

export async function fetchDesignJson(presignedUrl: string | null | undefined): Promise<Design | null> {
  if (!presignedUrl) return null;
  try {
    const res = await fetch(presignedUrl);
    if (!res.ok) {
      console.warn(`Failed to fetch design JSON: ${res.status}`);
      return null;
    }
    return await res.json();
  } catch (err) {
    console.error('Error fetching design JSON:', err);
    return null;
  }
}

export async function listTemplates(): Promise<IListTemplatesResponse> {
  const { data } = await api.get('templates/list');
  return data;
}

export async function getUpdateUrls(template_id: number | string): Promise<ITemplateUpdateUrls> {
  const { data } = await api.get('update-template-editor', { params: { template_id } });
  return unwrap<ITemplateUpdateUrls>(data, 'jsonUrl');
}

export async function confirmUpdate(
  id: number | string,
  payload: {
    post_status: string;
    file_secret: string;
    new_name: string;
    tag_name: string;
  },
): Promise<ITemplateMeta> {
  const { data } = await api.put(`template-editor-storage/${id}`, payload);
  return data;
}

export async function moveToTrash(id: number | string) {
  const { data } = await api.put(`template-editor-trash/${id}`);
  return data;
}


export async function deletePermanently(id: number | string) {
  const { data } = await api.delete(`template-editor-storage/${id}`);
  return data;
}


export async function saveTemplateFlow(params: {
  name: string;
  design: Design;
  tagName?: string;
  description?: string;
}): Promise<ITemplateMeta> {
  const { name, design, tagName = '', description = '' } = params;
  const safeName = name.toLowerCase().replace(/\s+/g, '-');

  // Step 1 — get upload URLs (requires Auth via api)
  const urls = await getUploadUrls(name, tagName);

  // Step 2 — prepare blobs
  const jsonBlob = new Blob([exportJson(design)], { type: 'application/json' });
  const htmlBlob = new Blob([exportHtml(design)], { type: 'text/html' });

  // Step 2 — upload both to S3 (no Auth). Both must return HTTP 204 before step 3.
  await Promise.all([
    uploadToS3(urls.jsonUrl, urls.jsonFields, jsonBlob, `${safeName}.json`),
    uploadToS3(urls.htmlUrl, urls.htmlFields, htmlBlob, `${safeName}.html`),
  ]);

  // Step 3 — confirm (requires Auth via api). Only reached if both uploads returned 204.
  return confirmSave({
    name,
    tag_name: tagName,
    description,
    template_key: urls.template_key,
    post_status: '204',
    file_secret: urls.file_secret,
    default: false,
  });
}

export async function updateTemplateFlow(params: {
  id: number | string;
  name: string;
  design: Design | null;
  tagName?: string;
}): Promise<ITemplateMeta> {
  const { id, name, design, tagName = '' } = params;
  const safeName = name.toLowerCase().replace(/\s+/g, '-');

  // Step 1 — get update URLs (requires Auth)
  const urls = await getUpdateUrls(id);

  if (design) {
    // Step 2 — prepare blobs
    const jsonBlob = new Blob([exportJson(design)], { type: 'application/json' });
    const htmlBlob = new Blob([exportHtml(design)], { type: 'text/html' });

    // Step 2 — upload sequentially: JSON first, then HTML (per spec)
    await uploadToS3(urls.jsonUrl, urls.jsonFields, jsonBlob, `${safeName}.json`);
    await uploadToS3(urls.htmlUrl, urls.htmlFields, htmlBlob, `${safeName}.html`);
  }

  // Step 3 — confirm (requires Auth). Only reached after both uploads return 204.
  return confirmUpdate(id, {
    post_status: '204',
    file_secret: urls.file_secret,
    new_name: name,
    tag_name: tagName,
  });
}
